import { Map, RecordCreate, Void } from "@pdfking/tfunc"

import type { CacheBackend } from "#public"

const store = "Default" as const
const version = 14

const to_promise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

export const IndexedDBStore = <V>(database: string): CacheBackend<V> => {
  const open = (mode: "readonly" | "readwrite" = "readwrite"): Promise<IDBObjectStore> => {
    const request = indexedDB.open(database, version)
    request.onupgradeneeded = () => {
      const db = request.result

      console.info(`IndexedDBStore: Upgrading database "${database}" to ${version}`)

      if (db.objectStoreNames.contains(store)) {
        db.deleteObjectStore(store)
        console.info(`IndexedDBStore: Cleared existing data in "${database}"`)
      }

      db.createObjectStore(store, { keyPath: "id" })
    }
    return to_promise(request).then(result => result.transaction(store, mode).objectStore(store))
  }

  return {
    clear: (): Promise<void> => open().then(db => to_promise(db.clear())),

    delete: (id: string): Promise<void> => open().then(db => to_promise(db.delete(id))),

    exists: (id: string): Promise<boolean> =>
      open("readonly")
        .then(db => to_promise(db.get(id)))
        .then(result => !!result),

    get: (id: string): Promise<undefined | V> =>
      open("readonly")
        .then(db => to_promise<undefined | { data: V; id: string }>(db.get(id)))
        .then(result => (result ? result.data : undefined)),

    // `getAll` is every record the store holds, which is what the database is
    // for: nothing here reads them one at a time.
    list: (): Promise<Record<string, V>> =>
      open("readonly")
        .then(db => to_promise<{ data: V; id: string }[]>(db.getAll()))
        .then(results => RecordCreate(Map(({ data, id }): [string, V] => [id, data], results))),

    set: (id: string, data: V): Promise<void> =>
      open()
        .then(db => to_promise(db.put({ data, id })))
        .then(Void),
  }
}
